import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStar, faGem } from '@/icons';
import { SignupProgress, TEAM_SIGNUP_STEPS } from '../components/SignupProgress';
import { SignupBackButton } from '../components/SignupBackButton';
import { useSignupSession } from '../contexts/SignupSessionContext';

type Plan = 'free' | 'basic' | 'pro';

interface PlanFeature {
    label: string;
    free: boolean;
    basic: boolean;
    pro: boolean;
}

const FEATURES: PlanFeature[] = [
    { label: 'Child Support Calculations', free: false, basic: true, pro: true },
    { label: 'Property Division Worksheets', free: false, basic: true, pro: true },
    { label: 'Child Support Summary & Worksheets', free: false, basic: true, pro: true },
    { label: 'Financial Affidavits', free: false, basic: true, pro: true },
    { label: 'Division Worksheets', free: false, basic: true, pro: true },
    { label: 'Team Collaboration', free: false, basic: true, pro: true },
    { label: 'Cloud-Based Access', free: false, basic: true, pro: true },
    { label: 'Split Custody Calculations', free: false, basic: false, pro: true },
    { label: 'High-Income Child Support Estimates', free: false, basic: false, pro: true },
    { label: 'Health Insurance Premium Calculator', free: false, basic: false, pro: true },
    { label: 'Monthly Expense Templates', free: false, basic: false, pro: true },
    { label: 'Clear Division Allocations', free: false, basic: false, pro: true },
    { label: 'Case File Sharing', free: false, basic: false, pro: true },
    { label: 'Notes on Child Support Summaries', free: false, basic: false, pro: true },
    { label: 'Firm-Branded Documents', free: false, basic: false, pro: true },
    { label: 'Send Expense Links to Clients', free: false, basic: false, pro: true },
];


export const RegisterPlanPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;
    const { session, updateSession } = useSignupSession();

    const [selectedPlan, setSelectedPlan] = useState<Plan>(session.plan && session.plan !== 'free' ? session.plan : 'pro');

    useEffect(() => {
        document.title = 'Select your plan | Calculist';
    }, []);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;

    const handleContinue = () => {
        updateSession({ plan: selectedPlan });
        navigate('/auth/register/team');
    };

    const cardStyle = (plan: Plan): React.CSSProperties => ({
        flex: 1,
        padding: theme.spacing.lg,
        borderRadius: theme.borderRadius.lg,
        border: `2px solid ${selectedPlan === plan ? theme.colors.primary : theme.colors.border.default}`,
        backgroundColor: selectedPlan === plan ? theme.colors.primary + '08' : theme.colors.surface,
        cursor: 'pointer',
        position: 'relative',
        transition: 'border-color 0.15s, background-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.sm,
        minWidth: '200px',
    });

    const fmt = (n: number) => `$${n.toLocaleString('en-US')}`;

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
            paddingTop: theme.spacing.xl,
        }}>
            <div style={{ ...themedStyles.card.default, maxWidth: '860px', width: '100%' }}>
                <SignupProgress currentStep={1} steps={TEAM_SIGNUP_STEPS} />

                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.md }} />
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Select your plan</h1>
                    <p style={{ ...themedStyles.text.muted, marginTop: 0, marginBottom: '4px' }}>All plans are billed annually and must have at least one attorney seat.</p>
                    <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Support staff members are always free.</p>
                </div>

                {/* Plan cards */}
                <div style={{
                    display: 'flex',
                    gap: theme.spacing.md,
                    flexWrap: 'wrap',
                    marginBottom: theme.spacing.xl,
                }}>
                    {/* Free — hidden for now, may re-enable later
                    <div
                        style={cardStyle('free')}
                        onClick={() => setSelectedPlan('free')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPlan('free'); } }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>Free</div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, marginTop: '4px' }}>$0</div>
                                <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.muted }}>forever</div>
                            </div>
                            <div style={{
                                width: '20px', height: '20px', borderRadius: '50%',
                                border: `2px solid ${selectedPlan === 'free' ? theme.colors.primary : theme.colors.border.default}`,
                                backgroundColor: selectedPlan === 'free' ? theme.colors.primary : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                {selectedPlan === 'free' && <FontAwesomeIcon icon={faCheck} style={{ color: '#fff', fontSize: '10px' }} />}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: theme.spacing.sm }}>
                            {FREE_FEATURES.map(f => (
                                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '11px', flexShrink: 0 }} />
                                    {f}
                                </div>
                            ))}
                        </div>
                    </div>
                    */}

                    {/* Basic */}
                    <div
                        style={cardStyle('basic')}
                        onClick={() => setSelectedPlan('basic')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPlan('basic'); } }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>Basic</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '4px' }}>
                                    <span style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.primary }}>{fmt(500)}</span>
                                    <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.secondary }}> / attorney / year</span>
                                </div>
                                <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.muted, textAlign: 'right' }}>(plus taxes and applicable card processing fees)</div>
                                <div style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: '#16a34a', marginTop: '4px' }}>Support Staff members are free</div>
                            </div>
                            <div style={{
                                width: '20px', height: '20px', borderRadius: '50%',
                                border: `2px solid ${selectedPlan === 'basic' ? theme.colors.primary : theme.colors.border.default}`,
                                backgroundColor: selectedPlan === 'basic' ? theme.colors.primary : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                {selectedPlan === 'basic' && <FontAwesomeIcon icon={faCheck} style={{ color: '#fff', fontSize: '10px' }} />}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: theme.spacing.sm }}>
                            {FEATURES.filter(f => f.basic).map(f => (
                                <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '11px', flexShrink: 0 }} />
                                    {f.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pro */}
                    <div
                        style={cardStyle('pro')}
                        onClick={() => setSelectedPlan('pro')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPlan('pro'); } }}
                    >
                        {/* Recommended badge */}
                        <div style={{
                            position: 'absolute',
                            top: '-12px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: theme.colors.primary,
                            color: '#fff',
                            fontSize: theme.typography.fontSize.xs,
                            fontWeight: theme.typography.fontWeight.semibold,
                            padding: '2px 12px',
                            borderRadius: '999px',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}>
                            <FontAwesomeIcon icon={faStar} style={{ fontSize: '9px' }} />
                            Recommended
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>
                                    <FontAwesomeIcon icon={faGem} style={{ color: theme.colors.primary, fontSize: '16px' }} /> Pro
                                </div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '4px' }}>
                                    <span style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.primary }}>{fmt(1000)}</span>
                                    <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.secondary }}> / attorney / year</span>
                                </div>
                                <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.muted, textAlign: 'right' }}>(plus taxes and applicable card processing fees)</div>
                                <div style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: '#16a34a', marginTop: '4px' }}>Support Staff members are free</div>
                            </div>
                            <div style={{
                                width: '20px', height: '20px', borderRadius: '50%',
                                border: `2px solid ${selectedPlan === 'pro' ? theme.colors.primary : theme.colors.border.default}`,
                                backgroundColor: selectedPlan === 'pro' ? theme.colors.primary : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                {selectedPlan === 'pro' && <FontAwesomeIcon icon={faCheck} style={{ color: '#fff', fontSize: '10px' }} />}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: theme.spacing.sm }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: '2px' }}>
                                <div style={{ flex: 1, height: '1px', backgroundColor: theme.colors.border.default }} />
                                <span style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary, whiteSpace: 'nowrap' }}>All Basic features plus</span>
                                <div style={{ flex: 1, height: '1px', backgroundColor: theme.colors.border.default }} />
                            </div>
                            {FEATURES.filter(f => f.pro && !f.basic).map(f => (
                                <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    <FontAwesomeIcon icon={faCheck} style={{ color: theme.colors.primary, fontSize: '11px', flexShrink: 0 }} />
                                    {f.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleContinue}
                    style={{
                        ...themedStyles.button.primary,
                        width: '100%',
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    }}
                >
                    Continue
                </button>
                <SignupBackButton onClick={() => navigate('/auth/login')} />
            </div>
        </div>
    );
};
